import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { XCircle, ArrowLeft, Home, RefreshCw } from 'lucide-react';
import { paymentsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';

const BookingCancelPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getAuthToken, isAuthenticated } = useAuth();
  const { toast } = useToast();
  
  const [cancelling, setCancelling] = useState(false);
  
  const bookingId = searchParams.get('booking_id');

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleCancelBooking = async () => {
    if (!bookingId) {
      toast({ title: 'Error', description: 'No booking to cancel', variant: 'destructive' });
      return;
    }

    setCancelling(true);
    try {
      const token = getAuthToken();
      await paymentsAPI.cancelPendingBooking(bookingId, token);
      toast({ title: 'Booking Cancelled', description: 'Your pending booking has been cancelled.' });
      navigate('/guest/browse');
    } catch (err) {
      console.error('Error cancelling booking:', err);
      toast({ 
        title: 'Cancel Failed', 
        description: err.response?.data?.detail || 'Unable to cancel booking. It may have already been processed.',
        variant: 'destructive'
      });
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F2E9] flex items-center justify-center p-4">
      <Card className="max-w-lg w-full shadow-xl" data-testid="booking-cancel-card">
        <CardContent className="p-8 text-center">
          <XCircle size={64} className="mx-auto text-amber-500 mb-6" />
          
          <h2 className="text-2xl font-bold text-gray-700 mb-4" data-testid="payment-cancelled-title">
            Payment Cancelled
          </h2>
          
          <p className="text-gray-600 mb-6">
            You cancelled the payment process. Your booking is still pending.
            You can either try again or cancel the booking.
          </p>
          
          <div className="flex flex-col gap-3">
            {bookingId && (
              <>
                <Button 
                  onClick={() => navigate(`/guest/browse?tab=upcoming`)}
                  className="w-full bg-[#1F3A68] hover:bg-[#1F3A68]/90"
                  data-testid="retry-payment-btn"
                >
                  <RefreshCw size={16} className="mr-2" />
                  View Booking & Retry Payment
                </Button>
                
                <Button 
                  variant="outline"
                  onClick={handleCancelBooking}
                  disabled={cancelling}
                  className="w-full text-red-600 hover:text-red-700 border-red-200 hover:border-red-300"
                  data-testid="cancel-booking-btn"
                >
                  {cancelling ? 'Cancelling...' : 'Cancel Booking'}
                </Button>
              </>
            )}
            
            <Button 
              variant="outline"
              onClick={() => navigate('/')}
              className="w-full"
            >
              <Home size={16} className="mr-2" />
              Back to Home
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BookingCancelPage;
